/**
 * Tier-1 "Understand" projection (spec 2026-07-04 §6): a MECHANICAL,
 * business-language view-model over the definition JSON. Nothing here is
 * hand-maintained prose about behavior — if it's in the VM, it came from
 * the definition, so it can never lie.
 *
 * Tolerant by design (spec §13): unknown top-level sections are surfaced
 * in `unrecognizedSections` instead of being silently dropped.
 */
import type {
  CheckpointSpec,
  EscalationSpecJson,
  EscalationTriggerJson,
  GraphWorkflowDefinitionJson,
  OrderWorkflowDefinitionJson,
  ProcessExitJson,
  ProcessStepJson,
  ProcessWorkflowDefinitionJson,
  SignalSpecJson,
  WorkflowDefinitionJson,
} from '@/types/workflowDefinition'
import { capabilityLabel, humanize } from '@/utils/friendlyLabels'

export interface AgendaItemVM {
  key: string
  label: string
  itemKind: 'need' | 'slot'
  slotType: string | null
  askPriority: number
  requiredness: 'always' | 'conditional' | 'optional'
  condition: string | null           // humanized required_when, or null
  asks: Record<string, string>       // language -> ask text ({} when none)
  promptHint: string | null
  options: string[] | null
}
export interface AutomationVM {
  key: string
  capability: string
  category: 'derived' | 'side_effect'
  when: string | null                // humanized, or null
  dependsOn: string[]                // labels where a slot label exists, else keys
  writes: string | null
  compensate: string | null          // compensation capability id
}
export interface CheckpointVM {
  key: string
  requires: string[]                 // labels where available, else keys
  requireKeys: string[]              // raw slot keys, parallel to `requires`
  templateKey: string
  text: Record<string, string>
}
export interface MessageVM {
  key: string
  group: 'template' | 'ask' | 'park' | 'fallback' | 'handoff'
  text: Record<string, string>
}
export interface HandoffRuleVM {
  id: string | null                  // null = trigger without a stable id (not overlay-addressable)
  kind: string
  kindLabel: string
  when: string
  mode: 'hard' | 'advisory' | 'notify'
  enabled: boolean
  description: string                // humanized `when`, business language
  keywords: Record<string, string[]> | null
  afterTurns: number | null
  resetAfterMinutes: number | null
  after: number
  /** `slot_above` limit (the rule fires on a value strictly above it). */
  above: number | null
  messageKey: string | null          // trigger.message, else 'default' when that key exists
  messageText: Record<string, string>
  /** 'switch' renders the "Switches to" badge; anything malformed or unknown
   * normalizes to 'escalate' (fail-safe, #282 sanitizeMatch precedent). */
  action: 'escalate' | 'switch'
  switchTarget: string | null
}
export interface HandoffMessageVM { key: string; text: Record<string, string> }
export interface HandoffVM { rules: HandoffRuleVM[]; messages: HandoffMessageVM[] }
export interface SignalVM {
  id: string | null                  // null = signal without a stable id (not overlay-addressable)
  on: string                         // lifecycle event: 'payment_received' | 'completion'
  instruction: Record<string, string> // language -> authored, customer-invisible text
  enabled: boolean
}
export interface OrderOverviewVM {
  kind: 'order'
  name: string
  description: string | null
  languages: string[]
  triggerCount: number
  cooldownMinutes: number | null
  agenda: AgendaItemVM[]
  automations: AutomationVM[]
  checkpoints: CheckpointVM[]
  messages: MessageVM[]
  handoff: HandoffVM
  signals: SignalVM[]
  unrecognizedSections: string[]
  /** key → explicit definition label (only keys that have one) — feeds
   * TokenChip/TemplateText label resolution (UI-consistency spec §2). */
  slotLabels: Record<string, string>
}
export interface GraphOverviewVM {
  kind: 'graph'
  name: string
  triggerCount: number
  nodeTypeCounts: Record<string, number>
  guardrails: { node: string; text: string }[]
}
/** One named exit of a process step (E1, spec 2026-09-18 §5). `target` is
 * the step id, the close outcome, or the notified seat id by `targetKind`;
 * `targetLabel` is the reader form ("11 Offer and negotiate",
 * "Close: not pursued", "Tell Commercial"). A back-edge points at a step
 * numbered at or before its source. */
export interface ProcessExitVM {
  name: string
  targetKind: 'step' | 'close' | 'notify'
  target: string
  targetLabel: string
  requires: string[]
  isBackEdge: boolean
}
export interface ProcessStepVM {
  id: string
  /** 1-based position in `steps` declaration order — derived, never stored. */
  number: number
  title: string
  lane: string
  laneLabel: string
  executorType: 'person' | 'ai' | 'automation' | 'conversation' | 'wait'
  /** Reader words for the executor: a seat label, "<seat>, with AI",
   * "Automation: …", "Conversation: …" or "Wait: …". */
  who: string
  /** SA-469 (spec D7): a conversation step's party (the grammar's default is `staff`); null for every other executor. */
  party: 'staff' | 'customer' | null
  level: string | null
  levelsOffered: string[]
  needs: { id: string; label: string }[]
  produces: { id: string; label: string }[]
  due: string | null
  /** Armed by a duration (`after`) or by a field's value (`fromFieldLabel`,
   * E4 `timeout.from_field`) — exactly one is set. */
  timeout: { after: string | null; fromFieldLabel: string | null; exit: string } | null
  exits: ProcessExitVM[]
  isEntry: boolean
}
export interface ProcessOverviewVM {
  kind: 'process'
  name: string
  lanes: { id: string; label: string }[]
  /** Reading order. */
  steps: ProcessStepVM[]
  seats: { id: string; label: string }[]
  entry: string
  /** SA-469: the intake agenda's workflow id (`record.agenda`) — the overview links it. */
  recordAgenda: string
  /** Reader label of the intake agenda that records the record
   * (`record.agenda`, e.g. "Shipment quote") — the page's "How it starts"
   * sentence reads this, never the raw document. */
  recordAgendaLabel: string
  unrecognizedSections: string[]
}
export type WorkflowOverviewVM = OrderOverviewVM | GraphOverviewVM | ProcessOverviewVM

const KNOWN_ORDER_SECTIONS = new Set([
  'kind', 'id', 'version', 'name', 'description', 'order_type', 'languages',
  'triggers', 'required_capabilities', 'slots', 'needs', 'derived',
  'side_effects', 'checkpoints', 'templates', 'conversation', 'hook_entries',
  'post_completion_cooldown_minutes', 'on_paused_message', 'on_post_completion',
  // Deterministic escalation rules (Increment B) — projected as the
  // `handoff` VM (Handoff tab, escalation-admin-UI spec 2026-07-10).
  'escalation',
  // Switch-lanes allowlist (Increment D) — same treatment as escalation:
  // no dedicated VM projection yet (admin UI is a fast-follow).
  'switchable_to',
  // Completed+handoff terminal for cartless intakes (quoted-item design
  // §4.3) — surfaced via the Handoff tab, no dedicated section VM.
  'completion',
  // Authored, customer-invisible per-language lifecycle instructions
  // (workflow signals spec 2026-07-11 §3.A) — projected as the `signals`
  // VM (Signals tab).
  'signals',
])

const PREDICATE_RE = /^\s*([a-z0-9_.]+)\s*(==|!=)\s*(['"])(.*?)\3\s*$/i
const BARE_SIGNAL_RE = /^[a-z][a-z0-9_]*$/

/**
 * Builds CheckpointVMs from a checkpoints map + templates map + label
 * resolver. Extracted so the Tier-2 Configure projection
 * (`workflowConfigView.ts`) can reuse this exact derivation on a
 * display-composed (base + overlay) input instead of re-implementing it.
 */
export function buildCheckpointVMs(
  checkpoints: Record<string, CheckpointSpec>,
  templates: Record<string, Record<string, string>>,
  labelFor: (key: string) => string,
): CheckpointVM[] {
  return Object.entries(checkpoints).map(
    ([key, cp]): CheckpointVM => ({
      key,
      requires: (cp.requires ?? []).map(labelFor),
      requireKeys: cp.requires ?? [],
      templateKey: cp.template,
      text: templates[cp.template] ?? {},
    }),
  )
}

export function humanizeCondition(
  predicate: string,
  labelFor: (key: string) => string,
): string {
  const match = PREDICATE_RE.exec(predicate)
  if (match) {
    const [, key, op, , literal] = match
    const verb = op === '==' ? 'is' : 'is not'
    return `only when ${labelFor(key)} ${verb} “${literal}”`
  }
  if (BARE_SIGNAL_RE.test(predicate.trim())) {
    return `only when ${labelFor(predicate.trim())}`
  }
  return predicate
}

const HANDOFF_KIND_LABELS: Record<string, string> = {
  quote_request: 'Quote request',
  capacity_unavailable: 'Availability issue',
  order_conversation_stuck: 'Stuck conversation',
  customer_request: 'Customer asked for a person',
  product_unavailable: 'Product unavailable',
  lead_time_rush: 'Rush-order request',
  cash_payment_request: 'Cash-payment request',
  nutrition_question: 'Nutrition question',
  coverage: 'Delivery coverage',
}

/**
 * One escalation trigger → business-language rule VM. Exported so the
 * Tier-2 Configure projection (`workflowConfigView.ts`) reuses this exact
 * derivation on a display-composed (base ⊕ overlay) trigger.
 */
export function buildHandoffRuleVM(
  trigger: EscalationTriggerJson,
  messages: Record<string, Record<string, string>>,
  labelFor: (key: string) => string,
): HandoffRuleVM {
  const after = trigger.after ?? 1
  let description: string
  switch (trigger.when) {
    case 'topic_denylist':
      description = 'The customer mentions one of these phrases'
      break
    case 'out_of_catalog':
      description = 'The customer asks for something that isn\'t in the catalog'
      break
    case 'unknown_slots_seen':
      description =
        'The conversation keeps needing details this workflow doesn\'t collect' +
        (after >= 2 ? ` (seen ${after} times)` : '')
      break
    case 'stalled': {
      description =
        trigger.after_turns != null
          ? `The conversation makes no progress for ${trigger.after_turns} turns`
          : 'The conversation makes no progress'
      if (trigger.reset_after_minutes != null) {
        description += ` (the count resets after ${trigger.reset_after_minutes} minutes of quiet)`
      }
      break
    }
    case 'slot_above':
      if (trigger.slot && trigger.above != null) {
        description = `The customer states ${labelFor(trigger.slot)}: more than ${trigger.above}`
        if (trigger.mid_order === 'ignore') {
          description += ' (only before anything is in the cart)'
        }
      } else {
        description = humanizeCondition(trigger.when, labelFor)
      }
      break
    default:
      description = humanizeCondition(trigger.when, labelFor)
  }
  const messageKey = trigger.message ?? ('default' in messages ? 'default' : null)
  const isSwitch =
    trigger.action === 'switch' && typeof trigger.target === 'string' && trigger.target.length > 0
  return {
    id: trigger.id ?? null,
    kind: trigger.kind,
    kindLabel: HANDOFF_KIND_LABELS[trigger.kind] ?? trigger.kind,
    when: trigger.when,
    mode: trigger.mode ?? 'hard',
    enabled: trigger.enabled ?? true,
    description,
    keywords: trigger.match ?? null,
    afterTurns: trigger.after_turns ?? null,
    resetAfterMinutes: trigger.reset_after_minutes ?? null,
    after,
    above: trigger.above ?? null,
    messageKey,
    messageText: (messageKey !== null ? messages[messageKey] : undefined) ?? {},
    action: isSwitch ? 'switch' : 'escalate',
    switchTarget: isSwitch ? (trigger.target as string) : null,
  }
}

export function buildHandoffVM(
  escalation: EscalationSpecJson | null | undefined,
  labelFor: (key: string) => string,
): HandoffVM {
  const messages = escalation?.messages ?? {}
  return {
    rules: (escalation?.triggers ?? []).map((t) =>
      buildHandoffRuleVM(t, messages, labelFor),
    ),
    messages: Object.entries(messages).map(([key, text]) => ({ key, text })),
  }
}

/**
 * One authored signal → VM. A pure field copy: signals carry no humanized
 * fields (their instruction text holds raw tokens resolved at render), so —
 * unlike `buildHandoffRuleVM` — no label resolver is threaded. Exported so
 * the Tier-2 Configure projection (`workflowConfigView.ts`) reuses this exact
 * derivation on a display-composed (base ⊕ overlay) signal.
 */
export function buildSignalVM(signal: SignalSpecJson): SignalVM {
  return {
    id: signal.id ?? null,
    on: signal.on,
    instruction: signal.instruction ?? {},
    enabled: signal.enabled ?? true,
  }
}

/**
 * Signals list → VM list. `labelFor` is accepted only for call-site parity
 * with `buildHandoffVM` (projectOrder passes the same resolver to every
 * builder); it is intentionally unused — see `buildSignalVM`.
 */
export function buildSignalsVM(
  signals: SignalSpecJson[] | null | undefined,
  _labelFor: (key: string) => string,
): SignalVM[] {
  return (signals ?? []).map(buildSignalVM)
}

function projectOrder(def: OrderWorkflowDefinitionJson): OrderOverviewVM {
  const slots = def.slots ?? {}
  const needs = def.needs ?? {}
  const templates = def.templates ?? {}
  const conversation = def.conversation ?? {}
  const asks = conversation.asks ?? {}

  const labelFor = (key: string): string =>
    slots[key]?.label ?? needs[key]?.label ?? key

  const slotLabels: Record<string, string> = {}
  for (const [key, slot] of Object.entries(slots)) {
    if (slot.label) slotLabels[key] = slot.label
  }
  for (const [key, need] of Object.entries(needs)) {
    if (need.label) slotLabels[key] = need.label
  }

  const agenda: AgendaItemVM[] = [
    ...Object.entries(needs).map(([key, need]): AgendaItemVM => ({
      key,
      label: need.label ?? key,
      itemKind: 'need',
      slotType: null,
      askPriority: need.ask_priority ?? 100,
      requiredness: 'always',
      condition: null,
      asks: asks[key] ?? {},
      promptHint: need.prompt_hint ?? null,
      options: null,
    })),
    ...Object.entries(slots).map(([key, slot]): AgendaItemVM => ({
      key,
      label: slot.label ?? key,
      itemKind: 'slot',
      slotType: slot.type,
      askPriority: slot.ask_priority ?? 100,
      requiredness: slot.always_required
        ? 'always'
        : slot.required_when
          ? 'conditional'
          : 'optional',
      condition: slot.required_when
        ? humanizeCondition(slot.required_when, labelFor)
        : null,
      asks: asks[key] ?? {},
      promptHint: slot.prompt_hint ?? null,
      options: slot.options ?? null,
    })),
  ].sort((a, b) => a.askPriority - b.askPriority || a.key.localeCompare(b.key))

  const automations: AutomationVM[] = [
    ...Object.entries(def.derived ?? {}).map(([key, d]): AutomationVM => ({
      key,
      capability: d.capability,
      category: 'derived',
      when: d.when ? humanizeCondition(d.when, labelFor) : null,
      dependsOn: (d.depends_on ?? []).map(labelFor),
      writes: d.writes ?? null,
      compensate: null,
    })),
    ...Object.entries(def.side_effects ?? {}).map(([key, s]): AutomationVM => ({
      key,
      capability: s.capability,
      category: 'side_effect',
      when: null,
      dependsOn: (s.depends_on ?? []).map(labelFor),
      writes: null,
      compensate: s.compensate ?? null,
    })),
  ]

  const checkpoints: CheckpointVM[] = buildCheckpointVMs(
    def.checkpoints ?? {},
    templates,
    labelFor,
  )

  const messages: MessageVM[] = [
    ...Object.entries(templates).map(([key, text]): MessageVM => ({
      key,
      group: 'template',
      text,
    })),
    ...Object.entries(asks).map(([key, text]): MessageVM => ({
      key: `ask:${key}`,
      group: 'ask',
      text,
    })),
    ...(Object.keys(conversation.park_message ?? {}).length
      ? [{ key: 'park_message', group: 'park' as const, text: conversation.park_message! }]
      : []),
    ...(Object.keys(conversation.fallback_reply ?? {}).length
      ? [{ key: 'fallback_reply', group: 'fallback' as const, text: conversation.fallback_reply! }]
      : []),
    ...Object.entries(def.escalation?.messages ?? {}).map(([key, text]): MessageVM => ({
      key: `handoff:${key}`,
      group: 'handoff',
      text,
    })),
  ]

  return {
    kind: 'order',
    name: def.name,
    description: def.description ?? null,
    languages: def.languages ?? [],
    triggerCount: (def.triggers ?? []).length,
    cooldownMinutes: def.post_completion_cooldown_minutes ?? null,
    agenda,
    automations,
    checkpoints,
    messages,
    handoff: buildHandoffVM(def.escalation, labelFor),
    signals: buildSignalsVM(def.signals, labelFor),
    unrecognizedSections: Object.keys(def).filter(
      (k) => !KNOWN_ORDER_SECTIONS.has(k),
    ),
    slotLabels,
  }
}

function projectGraph(def: GraphWorkflowDefinitionJson): GraphOverviewVM {
  const nodes = def.nodes ?? {}
  const nodeTypeCounts: Record<string, number> = {}
  const guardrails: { node: string; text: string }[] = []
  for (const [name, node] of Object.entries(nodes)) {
    nodeTypeCounts[node.type] = (nodeTypeCounts[node.type] ?? 0) + 1
    for (const text of node.guardrails ?? []) guardrails.push({ node: name, text })
  }
  return {
    kind: 'graph',
    name: def.name,
    triggerCount: (def.triggers ?? []).length,
    nodeTypeCounts,
    guardrails,
  }
}

const KNOWN_PROCESS_SECTIONS = new Set([
  'kind', 'id', 'version', 'name', 'description', 'languages', 'required_capabilities',
  'record', 'seats', 'lanes', 'fields', 'revision', 'fallback_seat', 'entry', 'steps',
])

/** English-first localized text with a humanized fallback. The viewer is an
 * admin surface (EN copy) — this is display resolution, not the SA-24
 * customer-facing chain. */
function localizedText(map: Record<string, string> | undefined, fallback: string): string {
  if (!map) return fallback
  return map.en ?? Object.values(map)[0] ?? fallback
}

/** '1d' + business → '1 business day'; '72h' → '72 hours'; unknown shapes pass through. */
export function durationText(
  d: { after: string; calendar?: 'clock' | 'business' } | null | undefined,
): string | null {
  if (!d) return null
  const m = /^(\d+)([smhdw])$/.exec(d.after)
  if (!m) return d.after
  const n = Number(m[1])
  const units: Record<string, string> = {
    s: 'second', m: 'minute', h: 'hour', d: d.calendar === 'business' ? 'business day' : 'day', w: 'week',
  }
  const unit = units[m[2]]
  return `${n} ${unit}${n === 1 ? '' : 's'}`
}

function lowerHumanize(id: string): string {
  return humanize(id).toLowerCase()
}

/** SA-469: an intake agenda's reader words — "shipment-quote" → "Shipment quote". The ONE
 * rule the map, the drawer, the overview, the library and the mailbox row name an agenda by. */
export function agendaLabel(id: string): string {
  return humanize(id.replace(/-/g, '_'))
}

function whoText(def: ProcessWorkflowDefinitionJson, step: ProcessStepJson): string {
  const ex = step.executor
  const seatLabel = (id: string | null | undefined) =>
    id ? localizedText(def.seats[id]?.label, humanize(id)) : ''
  switch (ex.type) {
    case 'person':
      return seatLabel(ex.seat)
    case 'ai':
      return `${seatLabel(ex.seat)}, with AI`
    case 'automation':
      return `Automation: ${capabilityLabel(ex.capability)}`
    case 'conversation':
      // SA-469 (spec D7): a staff party is the intake, never a customer conversation.
      return ex.party === 'customer' ? `Conversation: ${agendaLabel(ex.agenda)}` : `Intake: ${agendaLabel(ex.agenda)}`
    case 'wait':
      return 'event' in ex.on
        ? `Wait: ${lowerHumanize(ex.on.event)}`
        : `Wait: ${durationText(ex.on)}`
  }
}

/** E1 viewer projection (spec 2026-09-18 §5). Steps are numbered from
 * `steps` declaration order; exits resolve to reader labels; a back-edge
 * is an exit whose target is numbered at or before its source. Pages
 * render THIS, never the raw process document. */
/** The server reads a string exit (`done: next_step`) as `{to: next_step}`
 * (`_normalize_string_exits`), the shipped YAMLs are written that way, and an
 * author's draft is stored as typed — so every reader of a DRAFT's exits goes
 * through this. Published definitions are always the long form. */
export function normalizeExit(exit: ProcessExitJson | string): ProcessExitJson {
  return typeof exit === 'string' ? { to: exit } : exit
}

export function projectProcess(def: ProcessWorkflowDefinitionJson): ProcessOverviewVM {
  const order = Object.keys(def.steps)
  const numberOf = (id: string) => order.indexOf(id) + 1
  const fieldLabel = (id: string) => localizedText(def.fields[id]?.label, humanize(id))
  const stepLabel = (id: string) =>
    `${numberOf(id)} ${localizedText(def.steps[id]?.title, humanize(id))}`
  const exitVm = (fromId: string, name: string, raw: ProcessExitJson | string): ProcessExitVM => {
    const e = normalizeExit(raw)
    const requires = e.requires ?? []
    if (e.to) {
      return {
        name, targetKind: 'step', target: e.to, targetLabel: stepLabel(e.to), requires,
        isBackEdge: numberOf(e.to) <= numberOf(fromId),
      }
    }
    if (e.close) {
      return {
        name, targetKind: 'close', target: e.close,
        targetLabel: `Close: ${lowerHumanize(e.close)}`, requires, isBackEdge: false,
      }
    }
    const seat = e.notify ?? ''
    return {
      name, targetKind: 'notify', target: seat,
      targetLabel: `Tell ${localizedText(def.seats[seat]?.label, humanize(seat))}`,
      requires, isBackEdge: false,
    }
  }
  const steps: ProcessStepVM[] = order.map((id) => {
    const s = def.steps[id]
    return {
      id,
      number: numberOf(id),
      title: localizedText(s.title, humanize(id)),
      lane: s.lane,
      laneLabel: localizedText(def.lanes[s.lane]?.label, humanize(s.lane)),
      executorType: s.executor.type,
      who: whoText(def, s),
      party: s.executor.type === 'conversation' ? (s.executor.party ?? 'staff') : null,
      level: s.level?.default ?? null,
      levelsOffered: s.level?.offered ?? [],
      needs: (s.needs ?? []).map((f) => ({ id: f, label: fieldLabel(f) })),
      produces: (s.produces ?? []).map((f) => ({ id: f, label: fieldLabel(f) })),
      due: durationText(s.due),
      timeout: s.timeout
        ? {
            after: s.timeout.after ?? null,
            fromFieldLabel: s.timeout.from_field ? fieldLabel(s.timeout.from_field) : null,
            exit: s.timeout.exit,
          }
        : null,
      exits: Object.entries(s.exits).map(([name, e]) => exitVm(id, name, e)),
      isEntry: id === def.entry,
    }
  })
  return {
    kind: 'process',
    name: def.name,
    lanes: Object.entries(def.lanes).map(([id, l]) => ({ id, label: localizedText(l.label, humanize(id)) })),
    steps,
    seats: Object.entries(def.seats).map(([id, s]) => ({ id, label: localizedText(s.label, humanize(id)) })),
    entry: def.entry,
    recordAgenda: def.record.agenda,
    recordAgendaLabel: agendaLabel(def.record.agenda),
    unrecognizedSections: Object.keys(def).filter((k) => !KNOWN_PROCESS_SECTIONS.has(k)),
  }
}

/** A field as the step drawer names it (SA-459, spec §6.2). */
export interface StepDetailField {
  id: string
  label: string
  /** E5 (D57): the field carries `restricted`; the drawer draws a lock. */
  restricted: boolean
}

export interface StepDetailFormField extends StepDetailField {
  type: string
  options: string[] | null
  optional: boolean
  /** A `table` field's columns (SA-449), in declaration order. */
  columns?: { label: string; type: string }[]
}

/**
 * The step drawer's executor detail (SA-459, spec §6.2): everything the six
 * facts do not carry, from the definition. Pure; the drawer renders THIS.
 */
export interface StepDetailVM {
  kind: ProcessStepVM['executorType']
  seatLabel: string | null
  laneLabel: string
  needs: StepDetailField[]
  produces: StepDetailField[]
  /** The ONE place that words a timeout (spec §9): `when "<field>" passes the
   * step exits "<exit>"` or `after <duration> the step exits "<exit>"`. */
  timeoutSentence: string | null
  /** A person or ai step's form: `executor.form` when named, else the step's produces. */
  form: StepDetailFormField[]
  ai: { instruction: string; threshold: number; attachments: boolean; inputs: string[] } | null
  automation: { capability: string; operation: string | null; onFailure: string } | null
  wait: { event: string | null; after: string | null; timeout: string | null } | null
  conversation: { agendaLabel: string } | null
  levelsOffered: string[]
}

function timeoutSentence(def: ProcessWorkflowDefinitionJson, step: ProcessStepJson): string | null {
  const t = step.timeout
  if (!t || (!t.from_field && !t.after)) return null
  const when = t.from_field
    ? `when “${localizedText(def.fields[t.from_field]?.label, humanize(t.from_field))}” passes`
    : `after ${durationText({ after: t.after!, calendar: t.calendar })}`
  return `${when} the step exits “${t.exit.replace(/_/g, ' ')}”`
}

/** The reader label of a field id, humanized when the definition lacks it —
 * the drawer's requires row uses it. */
export function fieldLabelFor(def: ProcessWorkflowDefinitionJson, id: string): string {
  return localizedText(def.fields[id]?.label, humanize(id))
}

export function projectStepDetail(def: ProcessWorkflowDefinitionJson, stepId: string): StepDetailVM | null {
  const step = def.steps[stepId]
  if (!step) return null
  const field = (id: string): StepDetailField => ({
    id,
    label: fieldLabelFor(def, id),
    restricted: def.fields[id]?.restricted != null,
  })
  const seatLabel = (id: string | null | undefined): string | null =>
    id ? localizedText(def.seats[id]?.label, humanize(id)) : null
  const ex = step.executor
  const formIds = ex.type === 'person' || ex.type === 'ai' ? (ex.form ?? step.produces ?? []) : []
  const form: StepDetailFormField[] = formIds.map((id) => {
    const f = def.fields[id]
    const columns = f?.columns
      ? Object.entries(f.columns).map(([cid, c]) => ({ label: localizedText(c.label, humanize(cid)), type: c.type }))
      : undefined
    return {
      ...field(id),
      type: f?.type ?? 'string',
      options: f?.options ?? null,
      optional: f?.optional ?? false,
      ...(columns ? { columns } : {}),
    }
  })
  const timeout = timeoutSentence(def, step)
  return {
    kind: ex.type,
    seatLabel: ex.type === 'person' || ex.type === 'ai' || ex.type === 'wait' ? seatLabel(ex.seat) : null,
    laneLabel: localizedText(def.lanes[step.lane]?.label, humanize(step.lane)),
    needs: (step.needs ?? []).map(field),
    produces: (step.produces ?? []).map(field),
    timeoutSentence: timeout,
    form,
    ai:
      ex.type === 'ai'
        ? {
            instruction: localizedText(ex.instruction, ''),
            threshold: ex.threshold,
            attachments: ex.attachments,
            inputs: (ex.inputs ?? []).map((id) => field(id).label),
          }
        : null,
    automation:
      ex.type === 'automation'
        ? {
            capability: capabilityLabel(ex.capability),
            operation: typeof ex.args?.operation === 'string' ? ex.args.operation : null,
            onFailure: 'the “failed” exit',
          }
        : null,
    wait:
      ex.type === 'wait'
        ? {
            event: 'event' in ex.on ? lowerHumanize(ex.on.event) : null,
            after: 'after' in ex.on ? durationText(ex.on) : null,
            timeout,
          }
        : null,
    conversation: ex.type === 'conversation' ? { agendaLabel: agendaLabel(ex.agenda) } : null,
    levelsOffered: step.level?.offered ?? [],
  }
}

export function projectWorkflowOverview(
  def: WorkflowDefinitionJson,
): WorkflowOverviewVM {
  if (def.kind === 'process') return projectProcess(def as ProcessWorkflowDefinitionJson)
  return def.kind === 'order'
    ? projectOrder(def as OrderWorkflowDefinitionJson)
    : projectGraph(def as GraphWorkflowDefinitionJson)
}
